import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    FaClipboardList,
    FaHistory,
    FaClock,
    FaBookOpen,
    FaCheckCircle,
    FaExclamationCircle,
    FaRegArrowAltCircleRight,
    FaTrophy,
    FaSearch,
    FaFilter,
    FaLayerGroup,
    FaTimes,
    FaFileAlt,
    FaCheck,
    FaTimesCircle
} from 'react-icons/fa';
import { createPortal } from 'react-dom';
import { showError, showConfirm } from '../../../utils/Alert';
import Loader from '../../shared/Loader';
import EmptyState from '../../shared/EmptyState';
import AssessmentEngine from '../shared/AssessmentEngine';
import { API_URL } from '../../../config';
import './StudentAssessments.css';

const StudentAssessments = () => {
    const [activeTab, setActiveTab] = useState('upcoming');
    const [upcoming, setUpcoming] = useState([]);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeAssessment, setActiveAssessment] = useState(null);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);

    // Filter States
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [selectedCourse, setSelectedCourse] = useState('All');

    const fetchData = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const [upcomingRes, historyRes] = await Promise.all([
                axios.get('/api/student/assessments/upcoming', { headers: { Authorization: `Bearer ${token}` } }),
                axios.get('/api/student/assessments/history', { headers: { Authorization: `Bearer ${token}` } })
            ]);
            setUpcoming(upcomingRes.data);
            setHistory(historyRes.data);
        } catch (err) {
            showError('Unable to fetch', 'Could not retrieve your assessment records.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleStartAssessment = async (assessment) => {
        const isExam = assessment.contentType === 'Exam';
        const result = await showConfirm(
            isExam ? 'Start Evaluation?' : 'Open Assignment?',
            isExam
                ? 'This is a timed exam. Once you start, the timer cannot be paused. Are you ready?'
                : 'Do you want to begin this assignment now?',
            isExam ? 'Yes, Start Timer' : 'Yes, Open'
        );

        if (result.isConfirmed) {
            setActiveAssessment(assessment);
        }
    };

    const handleExitAssessment = () => {
        setActiveAssessment(null);
        fetchData(); // Refresh lists
    };

    const handleReviewClick = (record) => {
        setSelectedRecord(record);
        setIsReviewModalOpen(true);
    };

    // Derived Data: Filtered Lists
    const getFilteredData = (data) => {
        return data.filter(item => {
            const assessment = item.assessmentId || item; // history has assessmentId, upcoming is the lesson directly
            const course = item.courseId;

            const matchesSearch =
                assessment.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (course?.title && course.title.toLowerCase().includes(searchTerm.toLowerCase()));

            const matchesCategory = selectedCategory === 'All' || assessment.contentType === selectedCategory;
            const matchesCourse = selectedCourse === 'All' || course?._id === selectedCourse;

            return matchesSearch && matchesCategory && matchesCourse;
        });
    };

    const filteredUpcoming = getFilteredData(upcoming);
    const filteredHistory = getFilteredData(history);

    // Get unique courses for filter dropdown
    const uniqueCourses = Array.from(new Set([
        ...upcoming.map(item => JSON.stringify({ id: item.courseId?._id, title: item.courseId?.title })),
        ...history.map(item => JSON.stringify({ id: item.courseId?._id, title: item.courseId?.title }))
    ])).map(s => JSON.parse(s)).filter(c => c.id);

    if (activeAssessment) {
        return (
            <div className="assessment-overlay">
                <AssessmentEngine
                    assessment={activeAssessment}
                    onExit={handleExitAssessment}
                />
            </div>
        );
    }

    if (loading) return <Loader />;

    return (
        <div className="student-assessments-root">
            <div className="assessments-header">
                <h1>Assessments & Exams</h1>
                <p>Track your academic progress, attempt assignments, and review grades from one unified command center.</p>
            </div>

            <div className="assessments-tabs">
                <button
                    className={`tab-btn ${activeTab === 'upcoming' ? 'active' : ''}`}
                    onClick={() => { setActiveTab('upcoming'); setSearchTerm(''); setSelectedCategory('All'); setSelectedCourse('All'); }}
                >
                    <FaClipboardList style={{ marginRight: '8px' }} /> Pending Assessments
                </button>
                <button
                    className={`tab-btn ${activeTab === 'history' ? 'active' : ''}`}
                    onClick={() => { setActiveTab('history'); setSearchTerm(''); setSelectedCategory('All'); setSelectedCourse('All'); }}
                >
                    <FaHistory style={{ marginRight: '8px' }} /> Submission History
                </button>
            </div>

            {/* Premium Filter Bar */}
            <div className="modern-filters-bar">
                <div className="search-wrapper">
                    <FaSearch className="search-icon" />
                    <input
                        type="text"
                        placeholder="Search by title or course..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                <div className="filter-actions">
                    <div className="category-select-wrapper">
                        <FaFilter className="filter-icon" />
                        <select
                            value={selectedCategory}
                            onChange={(e) => setSelectedCategory(e.target.value)}
                        >
                            <option value="All">All Categories</option>
                            <option value="Quiz">Quizzes</option>
                            <option value="Assignment">Assignments</option>
                            <option value="Exam">Exams</option>
                        </select>
                    </div>

                    <div className="category-select-wrapper">
                        <FaLayerGroup className="filter-icon" />
                        <select
                            value={selectedCourse}
                            onChange={(e) => setSelectedCourse(e.target.value)}
                        >
                            <option value="All">All Courses</option>
                            {uniqueCourses.map(course => (
                                <option key={course.id} value={course.id}>{course.title}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            <div className="assessments-content">
                {activeTab === 'upcoming' ? (
                    filteredUpcoming.length > 0 ? (
                        <div className="assessment-grid">
                            {filteredUpcoming.map(item => (
                                <div key={item._id} className="assessment-master-card">
                                    <div className={`card-type-tag tag-${item.contentType?.toLowerCase()}`}>
                                        {item.contentType}
                                    </div>
                                    <span className="course-context">{item.courseId?.title}</span>
                                    <h3 className="assessment-title">{item.title}</h3>

                                    <div className="assessment-meta">
                                        <div className="meta-item">
                                            <FaClock /> <span>{item.timeLimit || 20} Min</span>
                                        </div>
                                        <div className="meta-item">
                                            <FaBookOpen /> <span>{item.questions?.length || 0} Questions</span>
                                        </div>
                                    </div>

                                    <div className="card-footer">
                                        <span className="marks-badge">{item.maxMarks || 100} Points</span>
                                        <button
                                            className="attempt-btn"
                                            onClick={() => handleStartAssessment(item)}
                                        >
                                            Begin Now <FaRegArrowAltCircleRight />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <EmptyState
                            icon={FaSearch}
                            title="No assessments found"
                            message="We couldn't find any pending items matching your search or filters. Try adjusting your criteria or checking all courses."
                            actionText="Clear All Filters"
                            onAction={() => { setSearchTerm(''); setSelectedCategory('All'); setSelectedCourse('All'); }}
                        />
                    )
                ) : (
                    filteredHistory.length > 0 ? (
                        <div className="assessment-grid">
                            {filteredHistory.map(record => (
                                <div key={record._id} className="assessment-master-card history-card" onClick={() => handleReviewClick(record)} style={{ cursor: 'pointer' }}>
                                    <div className={`card-type-tag tag-${record.assessmentId?.contentType?.toLowerCase()}`}>
                                        {record.assessmentId?.contentType}
                                    </div>
                                    <span className="course-context">{record.courseId?.title}</span>
                                    <h3 className="assessment-title">{record.assessmentId?.title}</h3>

                                    <div className="assessment-meta">
                                        <div className="meta-item">
                                            <FaHistory /> <span>Graded on {new Date(record.gradedAt || record.createdAt).toLocaleDateString()}</span>
                                        </div>
                                    </div>

                                    <div className="card-footer">
                                        <div className={`status-badge ${record.status === 'Graded' ? 'status-graded' : 'status-pending'}`}>
                                            {record.status === 'Graded' ? <FaCheckCircle /> : <FaExclamationCircle />}
                                            {record.status}
                                        </div>

                                        <div className="score-display">
                                            <span className="score-value">{record.obtainedMarks} / {record.totalMarks}</span>
                                            <span className="score-label">ACHIEVED MARKS</span>
                                        </div>
                                    </div>

                                    {record.feedback && (
                                        <div className="tutor-feedback">
                                            <strong>Tutor Feedback:</strong>
                                            <p>{record.feedback}</p>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <EmptyState
                            icon={searchTerm || selectedCategory !== 'All' || selectedCourse !== 'All' ? FaSearch : FaTrophy}
                            title={searchTerm || selectedCategory !== 'All' || selectedCourse !== 'All' ? "No matches found" : "No History Yet"}
                            message={searchTerm || selectedCategory !== 'All' || selectedCourse !== 'All'
                                ? "No submission records match your current filters. Try broadening your search."
                                : "You haven't submitted any assessments yet. Get started with your first one today!"}
                            actionText={searchTerm || selectedCategory !== 'All' || selectedCourse !== 'All' ? "Clear Filters" : null}
                            onAction={() => { setSearchTerm(''); setSelectedCategory('All'); setSelectedCourse('All'); }}
                        />
                    )
                )}
            </div>

            {/* REVIEW MODAL */}
            {isReviewModalOpen && selectedRecord && createPortal(
                <div className="modal-overlay open" onClick={() => setIsReviewModalOpen(false)}>
                    <div className="modal-content review-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <div className="header-info">
                                <span className={`type-badge tag-${selectedRecord.type.toLowerCase()}`}>{selectedRecord.type}</span>
                                <h2>{selectedRecord.assessmentId?.title} - Performance Review</h2>
                            </div>
                            <button className="modal-close-btn" onClick={() => setIsReviewModalOpen(false)}><FaTimes /></button>
                        </div>

                        <div className="modal-body">
                            <div className="review-summary-belt">
                                <div className="summary-stat">
                                    <span className="stat-label">Total Points</span>
                                    <span className="stat-value">{selectedRecord.obtainedMarks} / {selectedRecord.totalMarks}</span>
                                </div>
                                <div className="summary-stat">
                                    <span className="stat-label">Completion Status</span>
                                    <span className={`stat-value status-${selectedRecord.status.toLowerCase()}`}>{selectedRecord.status}</span>
                                </div>
                                <div className="summary-stat">
                                    <span className="stat-label">Submitted On</span>
                                    <span className="stat-value">{new Date(selectedRecord.createdAt).toLocaleDateString()}</span>
                                </div>
                            </div>

                            {selectedRecord.type === 'Assignment' ? (
                                <div className="assignment-review-content">
                                    <div className="review-section">
                                        <h4><FaFileAlt /> Your Submission</h4>
                                        <div className="submission-box">
                                            {selectedRecord.submissionFileUrl ? (
                                                <a href={`${API_URL}${selectedRecord.submissionFileUrl}`} target="_blank" rel="noreferrer" className="artifact-link">
                                                    Open Submitted Document
                                                </a>
                                            ) : <p className="no-data">No file attached</p>}

                                            {selectedRecord.submissionText && (
                                                <div className="student-notes">
                                                    <h5>Submission Notes:</h5>
                                                    <p>{selectedRecord.submissionText}</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {selectedRecord.feedback && (
                                        <div className="review-section feedback-section">
                                            <h4><FaCheckCircle /> Instructor Feedback</h4>
                                            <div className="feedback-balloon">
                                                {selectedRecord.feedback}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="exam-review-content">
                                    <h4>Performance Breakdown</h4>
                                    <div className="questions-review-list">
                                        {selectedRecord.assessmentId?.questions?.map((q, idx) => {
                                            const studentAnswer = selectedRecord.answers?.find(a => a.questionId === q._id?.toString() || a.questionId === q._id);
                                            const isCorrect = studentAnswer?.isCorrect;

                                            return (
                                                <div key={idx} className={`review-question-card ${isCorrect ? 'correct' : 'incorrect'}`}>
                                                    <div className="q-header">
                                                        <span className="q-num">Question {idx + 1}</span>
                                                        <span className={`match-badge ${isCorrect ? 'match' : 'mismatch'}`}>
                                                            {isCorrect ? <><FaCheck /> Correct</> : <><FaTimesCircle /> Incorrect</>}
                                                        </span>
                                                    </div>
                                                    <p className="q-text">{q.questionText}</p>

                                                    <div className="review-options">
                                                        <div className={`review-option ${isCorrect ? 'selected-correct' : 'selected-wrong'}`}>
                                                            <span className="opt-label">Your Answer:</span>
                                                            <span className="opt-val">{studentAnswer?.selectedOption || 'No answer provided'}</span>
                                                        </div>
                                                        {!isCorrect && (
                                                            <div className="review-option correct-key">
                                                                <span className="opt-label">Correct Solution:</span>
                                                                <span className="opt-val">{q.correctAnswer}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="modal-footer">
                            <button className="primary-btn" onClick={() => setIsReviewModalOpen(false)}>Close Review</button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default StudentAssessments;
